/**
 * PricingConfigVersionDialog — renders the append-only `versionHistory` returned
 * by the correspondents admin pricing endpoints (tier_margins / rtl_base_rates).
 * Each entry: { changedBy, changedDateTime, priorEffectiveDate, changes|priorRatesByTerm }.
 * Read-only; the endpoints don't support restore.
 */
import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";

const fmtVal = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export default function PricingConfigVersionDialog({
  open,
  onClose,
  title,
  load,
}) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    if (!open || !load) return;
    let alive = true;
    setEntries(null);
    Promise.resolve(load())
      .then((res) => {
        if (!alive) return;
        const hist = res?.versionHistory ?? res ?? [];
        setEntries(Array.isArray(hist) ? [...hist].reverse() : []);
      })
      .catch(() => alive && setEntries([]));
    return () => {
      alive = false;
    };
  }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
        {title || "Change history"}
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        {entries == null ? (
          <Flx center sx={{ py: 4 }}>
            <CircularProgress size={20} sx={{ color: "#1F5AF2" }} />
          </Flx>
        ) : entries.length === 0 ? (
          <Txt sx={{ fontSize: 13, color: "#94a3b8", py: 2 }}>
            No edit history yet.
          </Txt>
        ) : (
          <Flx column g={1.25} sx={{ mt: 1 }}>
            {entries.map((e, i) => {
              const changes = e?.changes || {};
              const keys = Object.keys(changes);
              return (
                <Box
                  key={i}
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: "#fcfcfd",
                  }}
                >
                  <Flx ac jb g={1}>
                    <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                      {e?.changedBy || "—"}
                    </Txt>
                    <Txt sx={{ fontSize: 11.5, color: "#94a3b8" }}>
                      {e?.changedDateTime
                        ? formatDateTime(e.changedDateTime, "datetime")
                        : "—"}
                    </Txt>
                  </Flx>
                  {keys.length > 0 ? (
                    <Box sx={{ mt: 0.75 }}>
                      {keys.map((k) => (
                        <Txt
                          key={k}
                          sx={{ fontSize: 12, color: "#475569", lineHeight: 1.6 }}
                        >
                          <b>{k}</b>: {fmtVal(changes[k]?.old)} →{" "}
                          {fmtVal(changes[k]?.new)}
                        </Txt>
                      ))}
                    </Box>
                  ) : e?.priorRatesByTerm ? (
                    <Txt sx={{ fontSize: 12, color: "#475569", mt: 0.75 }}>
                      Prior rates: {fmtVal(e.priorRatesByTerm)}
                    </Txt>
                  ) : null}
                </Box>
              );
            })}
          </Flx>
        )}
      </DialogContent>
    </Dialog>
  );
}
